import { createRouter, createWebHistory } from 'vue-router';

import { installChunkLoadRecovery } from './chunkLoadRecovery';
import { parseQuery, stringifyQuery } from './queryString.js';
import adnetAdminRoutes from './routes/adnet-admin.js';
import adunitRoutes from './routes/adunit';
import campaignPocRoutes from './routes/campaignPoc';
import errorRoutes from './routes/error.js';
import profileRoutes from './routes/profile.js';
import siteRoutes from './routes/site.js';
import syndicationDomainsRoutes from './routes/syndication-domains';
import targetRoutes from './routes/targets';

const Postbacks = () => import('@/views/Postbacks/index.vue');
const Campaigns = () => import('@/views/Campaign/index.vue');
const CampaignAdd = () => import('@/views/Campaign/AdvCampaign/CampaignAdd.vue');
const Creatives = () => import('@/views/Advertiser/Creatives/List.vue');
const Users = () => import('@/views/Campaign/Admin/Users.vue');
const Dashboard = () => import('@/views/Dashboard/index.vue');
const Stats = () => import('@/views/Stats/Admin/index.vue');
const Contacts = () => import('@/views/Contacts/index.vue');
const ApiClientTest = () => import('@/views/ApiClientTest/index.vue');
const Privacy = () => import('@/views/Privacy/index.vue');
const LandingGroupsList = () => import('@/views/Advertiser/Landings/List.vue');
const Payments = () => import('@/views/Payments/index.vue');
const PaymentsPubs = () => import('@/views/Payments/Publisher/pubPaymentsAdmin.vue');
const PaymentsSsp = () => import('@/views/Payments/Admin/sspPayments.vue');
const Marketplace = () => import('@/views/Advertiser/Marketplace/Marketplace.vue');
const ArbitrageDashboard = () => import('@/views/Dashboard/Admin/ArbitrageDashboard.vue');

import * as auth from '@/utils/auth.js';

const routes = [
  {
    path: '/',
    redirect: 'dashboard',
    hidden: true,
    meta: {
      title: 'dashboard',
      icon: 'hp-home',
      alias: 'db',
    },
  },
  {
    path: '/dashboard',
    component: Dashboard,
    name: 'dashboard',
    meta: {
      roles: ['publisher', 'advert-admin', 'manager'],
      title: 'dashboard',
      icon: 'hp-home',
      alias: 'db',
    },
  },
  {
    path: '/arbitrage-dashboard',
    component: ArbitrageDashboard,
    hidden: true,
    name: 'arbitrage-dashboard',
    meta: {
      title: 'Arbitrage dashboard',
      roles: ['advert-admin', 'manager'],
    },
  },
  {
    path: '/campaigns',
    component: Campaigns,
    name: 'campaigns',
    meta: {
      roles: ['advertiser', 'advert-admin', 'manager'],
      title: 'campaigns',
      icon: 'hp-campaign',
      alias: 'cm',
    },
  },
  {
    path: '/marketplace',
    component: Marketplace,
    name: 'marketplace',
    meta: {
      roles: ['advertiser', 'advert-admin', 'manager'],
      title: 'Marketplace',
      icon: 'hp-marketplace',
      alias: 'mp',
    },
  },
  {
    path: '/postbacks',
    component: Postbacks,
    name: 'postbacks',
    meta: {
      roles: ['advertiser', 'advert-admin', 'manager'],
      title: 'postbacks',
      icon: 'hp-message',
      alias: 'pb',
    },
  },
  {
    path: '/statistics',
    component: Stats,
    name: 'stats',
    meta: {
      roles: ['publisher', 'advertiser', 'ad-exchange', 'advert-admin', 'manager'],
      title: 'statistics',
      icon: 'hp-statistics',
      alias: 'st',
    },
  },
  {
    path: '/payments',
    component: Payments,
    name: 'payments',
    meta: {
      roles: ['advertiser', 'publisher', 'advert-admin', 'manager'],
      title: 'payments',
      icon: 'hp-dollar',
      alias: 'pay',
    },
  },
  {
    path: '/payments-publishers',
    component: PaymentsPubs,
    name: 'payments-pubs-admin',
    hidden: true,
    meta: {
      roles: ['advert-admin', 'manager'],
      title: 'payments',
      icon: 'hp-dollar',
      alias: 'payp',
    },
  },
  {
    path: '/payments-ssp',
    component: PaymentsSsp,
    name: 'payments-ssp-admin',
    hidden: true,
    meta: {
      roles: ['advert-admin', 'manager'],
      title: 'SSP payments',
      icon: 'hp-dollar',
      alias: 'payssp',
    },
  },
  {
    path: '/campaigns/add',
    component: CampaignAdd,
    hidden: true,
    name: 'campaign-add',
    meta: {
      title: 'Add campaign',
      roles: ['advertiser', 'publisher', 'advert-admin', 'manager'],
    },
  },
  {
    path: '/campaigns/:id/copy',
    component: CampaignAdd,
    hidden: true,
    name: 'campaign-copy',
    meta: {
      isCopy: true,
      title: 'Copy campaign',
      roles: ['advertiser', 'advert-admin', 'manager'],
    },
  },
  {
    path: '/creatives',
    component: Creatives,
    name: 'creatives-list',
    meta: {
      roles: ['advertiser', 'advert-admin', 'manager'],
      title: 'Creatives',
      icon: 'hp-pen-tool',
      alias: 'cr',
    },
  },
  {
    path: '/landings',
    component: LandingGroupsList,
    name: 'landings-list',
    meta: {
      roles: ['advertiser'],
      title: 'Landings',
      icon: 'hp-layout',
      alias: 'l',
    },
  },
  {
    path: '/campaigns/:id/edit',
    component: CampaignAdd,
    hidden: true,
    name: 'campaign-edit',
    meta: {
      title: 'Edit campaign',
      isEdit: true,
      roles: ['advertiser', 'publisher', 'advert-admin', 'manager'],
    },
  },
  ...siteRoutes,
  ...adunitRoutes,
  ...targetRoutes,
  ...syndicationDomainsRoutes,
  {
    path: '/users',
    component: Users,
    name: 'users',
    meta: {
      roles: ['admin', 'advert-admin', 'manager'],
      icon: 'hp-users',
      title: 'users',
      alias: 'u',
    },
  },
  ...adnetAdminRoutes,
  {
    path: '/contacts',
    component: Contacts,
    name: 'contacts',
    meta: {
      roles: ['publisher', 'advertiser', 'advert-admin', 'manager'],
      title: 'support',
      icon: 'hp-message-circle',
      alias: 'sup',
    },
  },
  {
    path: '/api-client-test',
    component: ApiClientTest,
    name: 'api-client-test',
    hidden: true,
    meta: {
      roles: ['advert-admin'],
      title: 'API Client Test',
    },
  },
  {
    path: '/privacy',
    component: Privacy,
    name: 'privacy',
    hidden: true,
  },
  ...profileRoutes,
  ...errorRoutes,
  ...(process.env.FF_CAMPAIGNS ? campaignPocRoutes : []),
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
  linkExactActiveClass: 'is-active',
  linkActiveClass: 'is-active-parent',
  parseQuery,
  stringifyQuery,
});

installChunkLoadRecovery(router);

export default router;
export { routes };
